import { html } from "lit-html";
import { useState } from "Shared/haunted/CustomHooks";
import { DynamicSettings } from "./DynamicSettings";
import { useSaveDynamicSettings } from "./useSaveDynamicSettings";
import { URL_SAFE_REGEX } from "Services/constants";

export interface Props {
	newSettings: DynamicSettings;
	setNewSettings: (settings: DynamicSettings) => void;
}

interface ValidationErrors {
	[index: number]: string;
}

export const useRetargetingUrls = (props: Props) => {
	const initialUrls = [...(props.newSettings.RetargetingUrls || [])];
	const [urls, setUrls] = useState<string[]>(initialUrls);
	const [errors, setErrors] = useState<ValidationErrors>({});

	const isUrlSafe = (value: string): boolean => {
		if (value === "") return true;
		return URL_SAFE_REGEX.test(value);
	};

	const hasDuplicates = (urlList: string[]): boolean => {
		const nonEmptyUrls = urlList.filter((url) => url.trim() !== "");
		const uniqueUrls = new Set(nonEmptyUrls);
		return nonEmptyUrls.length !== uniqueUrls.size;
	};

	const handleUrlChange = (index: number, value: string) => {
		const newUrls = [...urls];
		newUrls[index] = value;
		setUrls(newUrls);

		const nonEmptyUrls = newUrls.filter((url) => url.trim() !== "");
		props.setNewSettings({
			...props.newSettings,
			RetargetingUrls: nonEmptyUrls,
		});

		if (errors[index]) {
			const newErrors = { ...errors };
			delete newErrors[index];
			setErrors(newErrors);
		}
	};

	const handleAddUrl = () => {
		const newUrls = [...urls, ""];
		setUrls(newUrls);

		const nonEmptyUrls = newUrls.filter((url) => url.trim() !== "");
		props.setNewSettings({
			...props.newSettings,
			RetargetingUrls: nonEmptyUrls,
		});
	};

	const handleDeleteUrl = (index: number) => {
		if (urls.length <= 1) {
			return;
		}

		const confirmed = window.confirm(
			"Are you sure you want to delete this URL? (Note that you will also need to save the settings to apply the deletion.)"
		);
		if (!confirmed) {
			return;
		}

		const newUrls = urls.filter((_, i) => i !== index);
		setUrls(newUrls);

		const newErrors = { ...errors };
		delete newErrors[index];

		const adjustedErrors: ValidationErrors = {};

		Object.keys(newErrors).forEach((key) => {
			const errorIndex = parseInt(key);
			if (errorIndex > index) {
				adjustedErrors[errorIndex - 1] = newErrors[errorIndex];
			} else {
				adjustedErrors[errorIndex] = newErrors[errorIndex];
			}
		});

		setErrors(adjustedErrors);

		const nonEmptyUrls = newUrls.filter((url) => url.trim() !== "");
		props.setNewSettings({
			...props.newSettings,
			RetargetingUrls: nonEmptyUrls,
		});
	};

	const validate = (): boolean => {
		const newErrors: ValidationErrors = {};

		urls.forEach((url, index) => {
			if (url.trim() === "") {
				newErrors[index] = "This field is required";
			}
		});

		urls.forEach((url, index) => {
			if (url.trim() !== "" && !newErrors[index] && !isUrlSafe(url)) {
				newErrors[index] = "URL can only contain URL-safe characters";
			}
		});

		if (hasDuplicates(urls)) {
			const urlCounts = new Map<string, number[]>();
			urls.forEach((url, index) => {
				if (url.trim() !== "") {
					if (!urlCounts.has(url)) {
						urlCounts.set(url, []);
					}
					urlCounts.get(url).push(index);
				}
			});

			urlCounts.forEach((indices) => {
				if (indices.length > 1) {
					indices.forEach((index) => {
						if (!newErrors[index]) {
							newErrors[index] = "Duplicate URL detected";
						}
					});
				}
			});
		}

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0) {
			return false;
		}

		return true;
	};

	const saver = useSaveDynamicSettings({
		newSettings: props.newSettings,
		validate,
	});

	const inputTemplate = (url: string, index: number) => {
		const hasError = errors[index] !== undefined;
		const showDeleteButton = urls.length > 1;
		return html`
			<div class="mb-4">
				<div class="flex items-center gap-2">
					<ac-input
						.label=${`URL ${index + 1}`}
						.value=${url}
						.onInput=${(value: string) => handleUrlChange(index, value)}
						.error=${hasError}
					></ac-input>
					${showDeleteButton
						? html`
								<button
									type="button"
									class="small rounded bg-n-red text-white hover:bg-n-blue"
									@click=${() => handleDeleteUrl(index)}
									title="Delete this URL"
								>
									Delete
								</button>
						  `
						: ""}
				</div>
				${hasError ? html`<div class="mt-1 text-sm text-n-red">${errors[index]}</div>` : ""}
			</div>
		`;
	};

	const htmlTemplate = () => html`
		<section class="mt-5">
			<div class="mb-4 text-sm text-n-dark-gray">
				Add retargeting URLs below. Each URL must be unique and contain only URL-safe characters.
			</div>
			<div class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
				${urls.map((url, index) => inputTemplate(url, index))}
			</div>
			<button type="button" class="mb-4 bg-n-blue hover:bg-n-red hover:text-white" @click=${handleAddUrl}>
				Add URL
			</button>
			${saver.htmlTemplate()}
		</section>
	`;

	return { htmlTemplate };
};
